import { Check, PencilLine, RotateCcw, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Confirm } from '@/components/ui/Confirm';
import { Pill } from '@/components/ui/Pill';
import { useSession } from '@/lib/auth/useSession';
import { formatDate, formatDateTime } from '@/lib/format';
import { CLASS_LABEL, type Conflict, type Severity } from './api';
import { useConflict, useConflicts, useReopenConflict, useResolveConflict, type ConflictTab } from './queries';

/**
 * Conflict queue (P1-D11). We don't resolve divergences in SREG's data — we
 * route them: each item shows both sources and our proposed reading; confirm
 * is one action, correct is the exception. A resolution is stored as a rule so
 * the same divergence is never raised twice.
 */
export function ConflictsScreen() {
  const [tab, setTab] = useState<ConflictTab>('open');
  const list = useConflicts(tab);
  const [selected, setSelected] = useState<string | undefined>();

  useEffect(() => {
    if (list.data && !list.data.some((c) => c.id === selected)) setSelected(list.data[0]?.id);
  }, [list.data, selected]);

  return (
    <div className="flex h-full">
      <aside className="flex w-96 shrink-0 flex-col border-r border-line bg-surface-alt">
        <div className="px-4 pb-2 pt-6">
          <h1 className="font-display text-2xl font-semibold text-ink">Conflicts</h1>
          <p className="mt-1 text-xs text-ink-soft">
            Where SREG's sources disagree. Confirm our reading, or correct it.
          </p>
          <div className="mt-3 flex gap-1">
            {(['open', 'resolved', 'all'] as ConflictTab[]).map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={tab === t}
                onClick={() => setTab(t)}
                className={`rounded-lg px-3 py-1 text-sm font-semibold capitalize ${tab === t ? 'bg-ink text-white' : 'text-ink-soft hover:bg-surface'}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <ul className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
          {list.data?.length === 0 && (
            <li className="px-3 py-8 text-center text-sm text-ink-faint">
              {tab === 'open' ? 'Nothing open. ' : ''}No conflicts here.
            </li>
          )}
          {list.data?.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => setSelected(c.id)}
                className={`mb-1 w-full rounded-lg px-3 py-2.5 text-left ${selected === c.id ? 'bg-surface shadow-sm' : 'hover:bg-surface'}`}
              >
                <div className="flex items-center gap-2">
                  <SeverityPill s={c.severity} />
                  <span className="text-xs text-ink-faint">
                    {c.class} · {CLASS_LABEL[c.class]}
                  </span>
                  {c.status !== 'open' && (
                    <span className="ml-auto">
                      <StatusPill c={c} />
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm font-semibold leading-snug text-ink">{c.title}</p>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="min-w-0 flex-1 overflow-y-auto">{selected ? <ConflictDetail id={selected} /> : null}</section>
    </div>
  );
}

function SeverityPill({ s }: { s: Severity }) {
  const tone = s === 'high' ? 'bronze' : s === 'medium' ? 'neutral' : 'faint';
  return <Pill tone={tone}>{s}</Pill>;
}

function StatusPill({ c }: { c: Conflict }) {
  const tone = c.status === 'open' ? 'neutral' : c.status === 'suppressed' ? 'faint' : 'sage';
  return <Pill tone={tone}>{c.status}</Pill>;
}

function ConflictDetail({ id }: { id: string }) {
  const { data: c } = useConflict(id);
  if (!c) return <p className="p-8 text-sm text-ink-faint">Loading…</p>;
  const side = (s: 'a' | 'b') => (c.evidence ?? []).filter((e) => e.side === s);

  return (
    <div className="mx-auto max-w-3xl px-8 py-8">
      <div className="flex flex-wrap items-center gap-2">
        <SeverityPill s={c.severity} />
        <Pill>
          {c.class} · {CLASS_LABEL[c.class]}
        </Pill>
        <StatusPill c={c} />
        <span className="ml-auto text-xs text-ink-faint">
          {c.entityType} {c.entityId}
          {c.field ? ` · ${c.field}` : ''} · raised {formatDate(c.createdAt)}
        </span>
      </div>
      <h2 className="mt-3 font-display text-2xl font-semibold text-ink">{c.title}</h2>

      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        <SourceCard label="Source A" source={c.sourceA} value={c.valueA} evidence={side('a')} />
        <SourceCard label="Source B" source={c.sourceB} value={c.valueB} evidence={side('b')} />
      </div>

      <div className="mt-4 rounded-xl border border-sage-light bg-sage/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-sage-dark">Our reading</p>
        <p className="mt-1 text-[15px] leading-relaxed text-ink">{c.proposedInterpretation}</p>
        {c.proposedValue && (
          <p className="mt-2 text-sm text-ink-soft">
            Proposed value: <span className="font-semibold text-ink">{c.proposedValue}</span>
          </p>
        )}
      </div>

      {c.status === 'open' ? <ResolveActions c={c} /> : <Resolution c={c} />}

      {!!c.events?.length && (
        <div className="mt-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-faint">History</p>
          <ol className="mt-2 space-y-1.5 border-l border-line pl-4">
            {c.events.map((e) => (
              <li key={e.id} className="text-sm text-ink-soft">
                <span className="font-semibold capitalize text-ink">{e.action}</span> · {formatDateTime(e.createdAt)}
                {e.note && <span> — {e.note}</span>}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function SourceCard({
  label,
  source,
  value,
  evidence,
}: {
  label: string;
  source?: string;
  value?: string;
  evidence: Conflict['evidence'];
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-faint">{label}</p>
      <p className="mt-1 text-sm text-ink-soft">{source ?? '—'}</p>
      <p className="mt-2 break-words font-display text-lg font-semibold text-ink">{value ?? '—'}</p>
      {evidence.map((e, i) => (
        <p key={i} className="mt-2 border-t border-line-soft pt-2 text-xs text-ink-faint">
          {e.source}
          {e.ref ? ` · ${e.ref}` : ''}
          {e.page ? ` · p. ${e.page}` : ''}
          {e.excerpt && <span className="mt-1 block italic text-ink-soft">“{e.excerpt}”</span>}
        </p>
      ))}
    </div>
  );
}

function ResolveActions({ c }: { c: Conflict }) {
  const { locked } = useSession();
  const lock = locked('conflicts.resolve');
  const resolve = useResolveConflict();
  const [mode, setMode] = useState<'none' | 'correct' | 'suppress'>('none');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');

  return (
    <div className="mt-6">
      <div className="flex flex-wrap gap-2">
        <Button
          lockedReason={lock}
          disabled={resolve.isPending}
          onClick={() => resolve.mutate({ id: c.id, status: 'confirmed' })}
        >
          <Check size={15} /> Confirm our reading
        </Button>
        <Button
          variant="secondary"
          lockedReason={lock}
          onClick={() => setMode(mode === 'correct' ? 'none' : 'correct')}
        >
          <PencilLine size={15} /> Correct
        </Button>
        <div className="relative">
          <Button variant="ghost" lockedReason={lock} onClick={() => setMode('suppress')}>
            <EyeOff size={15} /> Suppress
          </Button>
          {mode === 'suppress' && (
            <Confirm
              title="Suppress this conflict?"
              body="It's marked not worth raising, and the same divergence won't be raised again."
              confirmLabel="Suppress"
              className="left-0 top-full mt-1"
              busy={resolve.isPending}
              onCancel={() => setMode('none')}
              onConfirm={() => resolve.mutate({ id: c.id, status: 'suppressed' }, { onSettled: () => setMode('none') })}
            />
          )}
        </div>
      </div>
      {mode === 'correct' && (
        <div className="mt-3 rounded-xl border border-line bg-surface p-4">
          <label className="block text-xs font-semibold text-ink-soft">
            The correct value
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-sage"
            />
          </label>
          <label className="mt-3 block text-xs font-semibold text-ink-soft">
            Why (optional)
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-sage"
            />
          </label>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setMode('none')}>
              Cancel
            </Button>
            <Button
              disabled={!value.trim() || resolve.isPending}
              onClick={() =>
                resolve.mutate(
                  { id: c.id, status: 'corrected', resolvedValue: value.trim(), note: note.trim() || undefined },
                  { onSuccess: () => setMode('none') },
                )
              }
            >
              Save correction
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Resolution({ c }: { c: Conflict }) {
  const { locked } = useSession();
  const reopen = useReopenConflict();
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-line bg-surface p-4">
      <p className="text-sm text-ink">
        <span className="font-semibold capitalize">{c.status}</span> {formatDateTime(c.resolvedAt)}
        {c.resolvedValue && (
          <>
            {' '}
            — value <span className="font-semibold">{c.resolvedValue}</span>
          </>
        )}
      </p>
      {c.resolutionNote && <p className="mt-1 text-sm text-ink-soft">{c.resolutionNote}</p>}
      <div className="relative mt-3">
        <Button variant="secondary" lockedReason={locked('conflicts.resolve')} onClick={() => setConfirming(true)}>
          <RotateCcw size={15} /> Reopen
        </Button>
        {confirming && (
          <Confirm
            title="Reopen this conflict?"
            body="It goes back in the queue, and the stored rule for it is retired."
            confirmLabel="Reopen"
            className="left-0 top-full mt-1"
            busy={reopen.isPending}
            onCancel={() => setConfirming(false)}
            onConfirm={() => reopen.mutate({ id: c.id }, { onSettled: () => setConfirming(false) })}
          />
        )}
      </div>
    </div>
  );
}
